// src/components/Dashboard/ToolsBar/DashboardObjects.tsx
import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip, IconButton, TextField } from '@mui/material';
import { Descendant } from 'slate';
import {
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  InsertPhotoOutlined as InsertPhotoOutlinedIcon,
  SmartButton as SmartButtonIcon,
  Public as PublicIcon,
  TextFields as TextFieldsIcon,
} from '@mui/icons-material';
import CategoryIcon from '@mui/icons-material/Category';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ObjectsOptions from './ObjectsOptions';
import './ToolsBar.css';
import { fontSize } from '../..';
import { RootState } from '../../redux';
import {
  setSelectedImageId,
  setImageElements,
  updateImageElement,
  addImageElement,
  addRichTextElement,
  setSelectedRichTextId,
} from '../../redux/TabTile/TabActions';
import { ImageElementType, RichTextElement } from '../../redux/TabTile/TabStateInterfaces';
import RichTextSidebarToolbar from './RichText/RichTextTollbar';
import { Editor } from 'slate';

interface DashBoardObjectsProps {
  setShowObjectsSidebar: React.Dispatch<React.SetStateAction<boolean>>;
  setShowImageUploadModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedObject: string | null;
  innerDimensions: { width: number; height: number };
  editor: Editor | null;
  editorChangeCounter?: number;
  setSelectedObject: (obj: string | null) => void;
  iconColor?: string;
  backgroundColor?: string;
  selectedTabBackgroundColor?: string;
}

const OBJECT_TYPES = [
  { name: 'image', icon: InsertPhotoOutlinedIcon, label: 'Add Image' },
  { name: 'button', icon: SmartButtonIcon, label: 'Add Button' },
  { name: 'text', icon: TextFieldsIcon, label: 'Add RichText' },
] as const;

const DEFAULT_BUTTON_CONFIG: Omit<ImageElementType, 'id' | 'x' | 'y'> = {
  //  default width and height in grid units
  width: 6,
  height: 2,
  type: 'icon-button',
  backgroundColor: undefined,
  backgroundAlpha: 100,
  iconScale: 1,
  iconColor: '#808080',
  iconStyle: 'filled',
  contentOrder: ['text'],
  borderColor: '#808080',
  text: 'Button',
  borderRadius: 12,
  containerProps: { borderRadius: 12 },
  imageUrl: '',
  tooltip: { enabled: true, text: '' },
};

const DashboardObjects: React.FC<DashBoardObjectsProps> = ({
  setShowObjectsSidebar,
  setShowImageUploadModal,
  selectedObject,
  setSelectedObject,
  innerDimensions,
  iconColor,
  backgroundColor,
  selectedTabBackgroundColor,
  editor,
  editorChangeCounter,
}) => {
  const dispatch = useDispatch();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const activeTabId = useSelector((state: RootState) => state.tabTileProps.selectedTabId);
  const tabTileProps = useSelector((state: RootState) => state.tabTileProps);
  const { dashGridSize } = tabTileProps;
  const gridSize = dashGridSize;

  const { selectedImageId, imageElements, richTextElements, richTextElementId } = useSelector(
    (state: RootState) => {
      const tabData = state.tabState.tabs[activeTabId]?.dashboardObjects || {};
      return {
        selectedImageId: tabData.selectedImageId || null,
        imageElements: tabData.imageElements || [],
        richTextElements: tabData.richTextElements || [],
        richTextElementId: tabData.richTextElementId || null,
      };
    },
  );

  useEffect(() => {
    if (imageElements.length === 0 && richTextElements.length === 0) {
      setSelectedObject(null);
      dispatch(setSelectedImageId(activeTabId, null));
      dispatch(setSelectedRichTextId(activeTabId, null));
    }
  }, [imageElements, richTextElements, activeTabId, dispatch, setSelectedObject]);

  const toolbarRef = useRef<HTMLDivElement>(null);

  const updateScaling = useCallback(
    (id: string, scalingType: string) => {
      dispatch(updateImageElement(activeTabId, id, { scaling: scalingType }));
    },
    [dispatch, activeTabId],
  );

  const selectedElement = useMemo(() => {
    if (selectedImageId) {
      return imageElements.find((el: any) => el.id === selectedImageId) || null;
    }
    if (richTextElementId) {
      return richTextElements.find((el: any) => el.id === richTextElementId) || null;
    }
    return null;
  }, [imageElements, selectedImageId, richTextElements, richTextElementId]);

  const selectedTitle = useMemo(() => {
    if (!selectedElement) return 'Objects';
    if (selectedElement.type === 'image') return 'Selected Image';
    if (selectedElement.type === 'icon-button') return 'Selected Button';
    return 'Selected Rich Text';
  }, [selectedElement]);

  const isClickInsideMUIOverlay = useCallback(
    (target: HTMLElement) =>
      !!target.closest('.MuiPopover-root, .MuiMenu-root, .MuiDialog-root, .MuiModal-root'),
    [],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !toolbarRef.current?.contains(target) &&
        !target.closest('.react-draggable') &&
        !isClickInsideMUIOverlay(target)
      ) {
        dispatch(setSelectedImageId(activeTabId, null));
        dispatch(setSelectedRichTextId(activeTabId, null));
        setSelectedObject(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [dispatch, activeTabId, isClickInsideMUIOverlay, setSelectedObject]);

  const handleObjectClick = useCallback(
    (objectName: (typeof OBJECT_TYPES)[number]['name']) => {
      const isSelected = objectName === selectedObject;
      setSelectedObject(isSelected ? null : objectName);
      dispatch(setSelectedImageId(activeTabId, null));
      dispatch(setSelectedRichTextId(activeTabId, null));
      if (isSelected) return;

      if (objectName === 'image') {
        setShowImageUploadModal(true);
      } else if (objectName === 'text') {
        const initialValue: Descendant[] = [
          { type: 'paragraph', children: [{ text: 'Enter your text here...', fontSize: 12 }] },
        ];
        const newTextElement: RichTextElement = {
          id: String(Date.now()),
          x: Math.round(innerDimensions.width / (2 * gridSize.x)),
          y: Math.round(innerDimensions.height / (2 * gridSize.y)),
          width: 12,
          height: 6,
          value: initialValue,
          borderRadius: 12,
          backgroundColor: '',
          fontSize: 12,
          fontFamily: 'Arial',
          color: '',
        };
        dispatch(addRichTextElement(activeTabId, newTextElement));
        dispatch(setSelectedRichTextId(activeTabId, newTextElement.id));
      } else if (objectName === 'button') {
        // Use the defined default width and height
        const gridWidth = DEFAULT_BUTTON_CONFIG.width;
        const gridHeight = DEFAULT_BUTTON_CONFIG.height;

        // Calculate a centered position in grid units
        const x = Math.floor((innerDimensions.width / gridSize.x - gridWidth) / 2);
        const y = Math.floor((innerDimensions.height / gridSize.y - gridHeight) / 2);

        const newButton: ImageElementType = {
          ...DEFAULT_BUTTON_CONFIG,
          id: `btn-${Date.now()}`,
          x,
          y,
        };
        dispatch(addImageElement(activeTabId, newButton));
        dispatch(setSelectedImageId(activeTabId, newButton.id));
      }
    },
    [
      selectedObject,
      setSelectedObject,
      setShowImageUploadModal,
      dispatch,
      activeTabId,
      innerDimensions,
      gridSize,
    ],
  );

  const titleSection = useMemo(
    () => (
      <div
        className="axisTitle"
        style={{
          paddingLeft: '0.4rem',
          paddingTop: '5px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <CategoryOutlinedIcon sx={{ fontSize: '18px', color: iconColor }} />
          <span
            style={{
              fontSize: fontSize.large,
              color: iconColor,
              fontWeight: 600,
              marginBottom: '2px',
            }}
          >
            {selectedTitle}
          </span>
        </div>
        <Tooltip
          title={isCollapsed ? 'Expand' : 'Collapse'}
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
              },
            },
          }}
        >
          <IconButton
            size="small"
            onClick={() => setIsCollapsed(!isCollapsed)}
            sx={{ fontSize: 16, marginRight: '0.2rem' }}
          >
            {isCollapsed ? (
              <KeyboardArrowDownIcon sx={{ fontSize: 16, color: iconColor }} />
            ) : (
              <KeyboardArrowUpIcon sx={{ fontSize: 16, color: iconColor }} />
            )}
          </IconButton>
        </Tooltip>
      </div>
    ),
    [isCollapsed, selectedObject, imageElements, selectedTitle],
  );

  const isAnyObjectSelected = selectedImageId || richTextElementId;
  const objectIconColor = iconColor === '#ffffff' ? iconColor : '#908f8fff';

  return (
    <div className="tileListContainer" style={{ paddingRight: 0 }}>
      {titleSection}

      {!isCollapsed && (
        <>
          {/* Show icons only if no object is selected */}
          {!isAnyObjectSelected && (
            <div className="objectIconsContainer">
              {OBJECT_TYPES.map(({ name, icon: Icon, label }) => (
                <Tooltip
                  title={label}
                  key={name}
                  slotProps={{
                    tooltip: {
                      sx: { fontSize: '12px' },
                    },
                  }}
                >
                  <div
                    className="objectsIconWrapper"
                    onClick={() => handleObjectClick(name)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => ['Enter', ' '].includes(e.key) && handleObjectClick(name)}
                  >
                    <Icon
                      className={`objectIcon ${selectedObject === name ? 'selected' : ''}`}
                      fontSize="medium"
                      style={{ cursor: 'pointer', color: objectIconColor }}
                    />
                  </div>
                </Tooltip>
              ))}
            </div>
          )}

          {/* Show ObjectsOptions for images/buttons */}
          {/* Conditionally render property toolbars to ensure only one is visible */}
          {selectedImageId && imageElements.length > 0 ? (
            <div className="tools-bar-wrapper" ref={toolbarRef}>
              <ObjectsOptions
                iconColor={iconColor}
                backgroundColor={backgroundColor}
                selectedTabBackgroundColor={selectedTabBackgroundColor}
                selectedImageId={selectedImageId}
                imageElements={imageElements}
                onUpdateImageElements={(updatedList) =>
                  dispatch(setImageElements(activeTabId, updatedList))
                }
                updateScaling={updateScaling}
              />
            </div>
          ) : richTextElementId && richTextElements.length > 0 ? (
            <div className="tools-bar-wrapper" ref={toolbarRef}>
              <RichTextSidebarToolbar
                editor={editor}
                editorChangeCounter={editorChangeCounter}
                color={iconColor}
                tabId={activeTabId}
                richTextElements={richTextElements}
                selectedRichTextId={richTextElementId}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default DashboardObjects;
